import React, { forwardRef, startTransition, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ChevronRightIcon } from './NgSideNavIcon';

import {
  featureFlagsMap,
  hasActiveFeatureFlag,
  hasAnyRole,
  platformSelectors,
  useHasActiveFeatureFlag,
  useMountedEffect,
} from '@clatter/platform';
import { routesNames } from '../../../routes';
import { pagesMap } from '../../../constants';
import { FolderManagerItemTypes } from '../../../interfaces';
import { useGetUserRootFoldersQuery } from '../../../services';
import { assetManagerUISelectors, authSelectors, setSidebarStateItems } from '../../../store';

import { getNavIconName } from './NgSideNavIcon';
import { NAV_CONFIG, NavEntry, NavGroup, NavLeaf } from './navConfig';
import { NavFlyout, ActiveFlyout, ResolvedFlyout, ResolvedTooltip } from './NavFlyout';
import { useCustomPagesWithOptum } from '../../../pages/CustomPage/useCustomPagesWithOptum';

const COLLAPSED_WIDTH = 55;
const EXPANDED_WIDTH = 256;
const FLYOUT_LEFT_COLLAPSED = 65;
const FLYOUT_LEFT_EXPANDED = 266;
const FLYOUT_HIDE_DELAY_MS = 150;

const SideNavContainer = styled.div<{ $expanded: boolean; $topNavbarHeight: number | undefined }>`
  position: sticky;
  top: ${({ $topNavbarHeight }) => $topNavbarHeight}px;
  left: 0;
  height: calc(100vh - ${({ $topNavbarHeight }) => $topNavbarHeight}px);
  width: ${({ $expanded }) => ($expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH)}px;
  min-width: ${({ $expanded }) => ($expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH)}px;
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  background: ${({ theme }) => theme?.palette?.white ?? '#fff'};
  border-right: none;
  font-family: ${({ theme }) => theme?.fontFamily?.main ?? 'sans-serif'};
  transition: width 0.3s ease, min-width 0.3s ease;
  z-index: 10;
  overflow: visible;
  box-shadow:  ${({ theme }) => theme.shadows.lg};

  &:hover .expand-trigger {
    opacity: 1;
  }
`;

const NavList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const NavItem = styled.li<{ $expanded: boolean; $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $expanded }) => ($expanded ? 'flex-start' : 'center')};
  padding: ${({ $expanded }) => ($expanded ? '12px 16px' : '12px 0')};
  cursor: pointer;
  transition: background 0.2s;
  color: ${({ theme }) => theme?.palette?.text ?? '#000'};
  background: ${({ $active }) => ($active ? 'rgba(0,0,0,0.08)' : 'transparent')};

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: ${({ theme }) => theme?.palette?.text ?? '#000'};
    stroke: currentColor;
    fill: none;

    path,
    circle,
    rect,
    polyline,
    line {
      stroke-width: ${({ theme }) => theme?.size?.iconStrokeWidth ?? '1.25'};
    }
  }
`;

const NavLabel = styled.span<{ $visible: boolean }>`
  margin-left: 12px;
  white-space: nowrap;
  font-size: ${({ theme }) => theme?.fontSize?.default ?? '1rem'};
  color: ${({ theme }) => theme?.palette?.text ?? '#000'};
  display: ${({ $visible }) => ($visible ? 'inline' : 'none')};
`;

const NavChevron = styled.span`
  margin-left: auto;
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme?.palette?.darkGrey ?? '#666'};
  }
`;

const ExpandTrigger = styled.div<{ $expanded: boolean }>`
  width: 32px;
  height: 32px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme?.palette?.text ?? '#000'};
  border-radius: ${({ theme }) => theme?.radius?.xSmall ?? '0.30rem'};
  margin-left: 11px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: background 0.2s, opacity 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme?.palette?.text ?? '#000'};
    transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
    transition: transform 0.3s ease;
    stroke: currentColor;
    fill: none;
  }
`;

interface NgSideNavWithSubmenusProps {
  topNavbarHeight: number | undefined;
}

export const NgSideNavWithFlyout = forwardRef<HTMLDivElement, NgSideNavWithSubmenusProps>(
  ({ topNavbarHeight }, ref) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { slug: slugFromParams } = useParams();

    const activeUser = useSelector(authSelectors.getUserAuthState)?.user;
    const isAdmin = useSelector(authSelectors.isAdmin);
    const isContentManager = useSelector(authSelectors.isContentManager);
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
    const [activeFlyout, setActiveFlyout] = useState<ActiveFlyout | null>(null);
    const flyoutHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { data: sidebarFoldersData, isLoading } = useGetUserRootFoldersQuery();
    const sidebarState = useSelector(assetManagerUISelectors.getSidebarState);
    const featureFlags = useSelector(platformSelectors.featureFlag.selectAllFeatureFlags);

    const { customPages } = useSelector(platformSelectors.systemProfile.selectSystemProfile);
    const customPageItems = useCustomPagesWithOptum(customPages);

    const displayEntitiesTab = useHasActiveFeatureFlag(featureFlagsMap.MSM.micrositesEntities);
    const enableReports = useHasActiveFeatureFlag(featureFlagsMap.NG.reports);
    const enableReportsDocumentsGenerated = useHasActiveFeatureFlag(featureFlagsMap.NG.reportsDocumentsGenerated);
    const enableReportsFilesDownloaded = useHasActiveFeatureFlag(featureFlagsMap.NG.reportsFilesDownloaded);
    const enableCuratedCategories = useHasActiveFeatureFlag(featureFlagsMap.NG.curatedCategories);
    const foldersAreCaterogies = useHasActiveFeatureFlag(featureFlagsMap.NG.foldersAreCaterogies);

    const isSlidespacesEnabled = hasActiveFeatureFlag({
      flagName: featureFlagsMap.NG.slidespaces,
      featureFlags: featureFlags,
    });

    const isWorkspacesEnabled = hasActiveFeatureFlag({
      flagName: featureFlagsMap.NG.workspaces,
      featureFlags: featureFlags,
    });

    const showFlyout = useCallback((key: string, anchorElement: HTMLElement, resolved: ResolvedFlyout) => {
      if (flyoutHideTimer.current) clearTimeout(flyoutHideTimer.current);
      const rect = anchorElement.getBoundingClientRect();
      setActiveFlyout({ key, itemTop: rect.top - 10, itemCenterY: rect.top + rect.height / 2, resolved });
    }, []);

    const scheduleFlyoutClose = useCallback(() => {
      flyoutHideTimer.current = setTimeout(() => setActiveFlyout(null), FLYOUT_HIDE_DELAY_MS);
    }, []);

    const cancelFlyoutClose = useCallback(() => {
      if (flyoutHideTimer.current) clearTimeout(flyoutHideTimer.current);
    }, []);

    const closeFlyout = useCallback(() => setActiveFlyout(null), []);

    const navigateTo = useCallback(
      (item: any) => {
        closeFlyout();
        if (item?.externalUrl) {
          window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
          return;
        }
        if (item?.slug === 'entities') {
          startTransition(() => navigate(routesNames.entities));
          return;
        }
        if (item?.slug) {
          startTransition(() => navigate(item.slug.startsWith('/') ? item.slug : `/${item.slug}`));
        }
      },
      [closeFlyout, navigate],
    );

    useMountedEffect(() => {
      if (isLoading) return;

      const folderItems = (sidebarFoldersData || [])
        .map((item: any) => {
          const isEntities =
            item?.displayName?.toLowerCase() === 'entities' || item?.name?.toLowerCase() === 'entities';
          if (isEntities) {
            return displayEntitiesTab
              ? {
                  ...item,
                  id: undefined,
                  type: undefined,
                  slug: 'entities',
                  displayName: item.displayName || item.name,
                  icon: getNavIconName(item.slug),
                }
              : null;
          }
          if ((item?.isThemeFolder || item?.isStarterDeckFolder) && !isAdmin && !isContentManager) return null;
          return { ...item, displayName: item.displayName || item.name, icon: getNavIconName(item.slug) };
        })
        .filter(Boolean);

      const allItems: any[] = [
        { slug: 'home', displayName: 'Home', icon: getNavIconName('my-workspace') },
        ...folderItems,

        hasAnyRole(pagesMap.users.accessRoles, activeUser)
          ? {
              slug: pagesMap.users.slug,
              displayName: pagesMap.users.displayName,
              icon: getNavIconName(pagesMap.users.slug),
            }
          : null,

        hasAnyRole(pagesMap.groups.accessRoles, activeUser)
          ? {
              slug: pagesMap.groups.slug,
              displayName: pagesMap.groups.displayName,
              icon: getNavIconName(pagesMap.groups.slug),
            }
          : null,

        {
          slug: 'shared',
          displayName: 'Shared With Me',
          icon: getNavIconName('shared'),
          type: FolderManagerItemTypes.FOLDER,
        },

        isWorkspacesEnabled && hasAnyRole(pagesMap.workspaceTemplates.accessRoles, activeUser)
          ? {
              slug: pagesMap.workspaceTemplates.slug,
              displayName: pagesMap.workspaceTemplates.displayName,
              icon: getNavIconName(pagesMap.workspaceTemplates.slug),
            }
          : null,

        isSlidespacesEnabled && hasAnyRole(pagesMap.slidespaces.accessRoles, activeUser)
          ? {
              slug: pagesMap.slidespaces.slug,
              displayName: pagesMap.slidespaces.displayName,
              icon: getNavIconName(pagesMap.workspaceTemplates.slug),
            }
          : null,

        enableCuratedCategories && !foldersAreCaterogies && hasAnyRole(pagesMap.categories.accessRoles, activeUser)
          ? {
              slug: pagesMap.categories.slug,
              displayName: pagesMap.categories.displayName,
              icon: getNavIconName(pagesMap.categories.slug),
            }
          : null,

        hasAnyRole(pagesMap.settings.accessRoles, activeUser)
          ? {
              slug: pagesMap.settings.slug,
              displayName: pagesMap.settings.displayName,
              icon: getNavIconName(pagesMap.settings.slug),
            }
          : null,

        enableReports && hasAnyRole(pagesMap.reports.accessRoles, activeUser)
          ? {
              slug: pagesMap.reports.slug,
              displayName: pagesMap.reports.displayName,
              icon: getNavIconName(pagesMap.reports.slug),
              children: [
                enableReportsDocumentsGenerated && {
                  slug: `${routesNames.reports}/${routesNames.reportsDocumentsGenerated}`,
                  displayName: 'Documents Generated',
                  icon: getNavIconName('chart'),
                },
                enableReportsFilesDownloaded && {
                  slug: `${routesNames.reports}/${routesNames.reportsFilesDownloaded}`,
                  displayName: 'Files Downloaded',
                  icon: 'ShowChartIcon',
                },
              ].filter(Boolean),
            }
          : null,

        { slug: 'trash', displayName: 'Trash', icon: getNavIconName('trash') },
      ].filter(Boolean);

      dispatch(setSidebarStateItems(allItems));
    }, [
      isLoading,
      slugFromParams,
      displayEntitiesTab,
      sidebarFoldersData,
      enableReports,
      isSlidespacesEnabled,
      enableReportsFilesDownloaded,
      enableReportsDocumentsGenerated,
      enableCuratedCategories,
      foldersAreCaterogies,
      activeUser,
      isAdmin,
      isContentManager,
    ]);

    const serverItems: any[] = sidebarState?.items || [];
    const itemsBySlug = Object.fromEntries(serverItems.map((item: any) => [item.slug, item]));

    const resolveGroupFlyout = useCallback(
      (configEntry: NavGroup): ResolvedFlyout | null => {
        if (configEntry.sections) {
          const resolvedSections = configEntry.sections
            .map((section) => ({
              header: section.header,
              items: section.slugs.map((slug) => itemsBySlug[slug]).filter(Boolean),
            }))
            .filter((section) => section.items.length > 0);

          if (resolvedSections.length === 0) return null;
          return { kind: 'sectioned', title: configEntry.flyoutTitle, sections: resolvedSections };
        }

        if (configEntry.childSlugs) {
          const flatItems = configEntry.childSlugs.flatMap((slug) => {
            const found = itemsBySlug[slug];
            if (!found) return [];
            if (found.children?.length) return found.children;
            return [found];
          });

          if (flatItems.length === 0) return null;
          return {
            kind: 'flat',
            title: configEntry.flyoutTitle,
            titleSlug: configEntry.flyoutTitleSlug,
            items: flatItems,
          };
        }

        return null;
      },
      [itemsBySlug],
    );

    const resolveLeafTooltip = useCallback(
      (configEntry: NavLeaf): ResolvedTooltip => ({
        kind: 'tooltip',
        label: configEntry.label,
        onClickNavigate: () => navigateTo(itemsBySlug[configEntry.slug] ?? { slug: configEntry.slug }),
      }),
      [itemsBySlug, navigateTo],
    );

    const isEntryActive = (configEntry: NavEntry): boolean => {
      if (configEntry.type === 'leaf') {
        const item = itemsBySlug[configEntry.slug];
        const slug = item?.slug ?? configEntry.slug;
        return location.pathname.startsWith(slug.startsWith('/') ? slug : `/${slug}`);
      }
      if (configEntry.sections) {
        return configEntry.sections
          .flatMap((section) => section.slugs)
          .some((slug) => location.pathname.startsWith(`/${slug}`));
      }
      if (configEntry.childSlugs) {
        return configEntry.childSlugs.some((slug) => {
          const item = itemsBySlug[slug];
          if (!item) return false;
          if (item.children?.length) {
            return item.children.some((child: any) =>
              location.pathname.startsWith(child.slug.startsWith('/') ? child.slug : `/${child.slug}`),
            );
          }
          return location.pathname.startsWith(`/${slug}`);
        });
      }
      return false;
    };

    const makeHoverHandlers = (key: string, resolved: ResolvedFlyout) => ({
      onMouseEnter: (event: React.MouseEvent<HTMLLIElement>) => showFlyout(key, event.currentTarget, resolved),
      onMouseLeave: scheduleFlyoutClose,
    });

    const flyoutLeft = isSidebarExpanded ? FLYOUT_LEFT_EXPANDED : FLYOUT_LEFT_COLLAPSED;

    return (
      <>
        <SideNavContainer ref={ref} $expanded={isSidebarExpanded} $topNavbarHeight={topNavbarHeight}>
          <NavList>
            {NAV_CONFIG.filter((configEntry) => configEntry.key !== 'admin' || isAdmin || isContentManager).map((configEntry) => {
              if (configEntry.key === 'custom-page') {
                const isActive = location.pathname.startsWith('/custom-page');

                if (customPageItems.length === 0) return null;

                if (customPageItems.length <= 1) {
                  const only = customPageItems[0];
                  const target =
                    customPageItems.length === 1
                      ? only.external
                        ? { externalUrl: only.url }
                        : { slug: `/custom-page/${only.ID}` }
                      : { slug: '/custom-page' };
                  const label = only?.DisplayName ?? configEntry.label;
                  const tooltip: ResolvedTooltip = {
                    kind: 'tooltip',
                    label,
                    onClickNavigate: () => navigateTo(target),
                  };
                  return (
                    <NavItem
                      key={configEntry.key}
                      $expanded={isSidebarExpanded}
                      $active={isActive}
                      onClick={() => navigateTo(target)}
                      {...makeHoverHandlers(configEntry.key, tooltip)}
                    >
                      <configEntry.Icon />
                      <NavLabel $visible={isSidebarExpanded}>{label}</NavLabel>
                    </NavItem>
                  );
                }

                const flyout = {
                  kind: 'flat' as const,
                  title: 'Custom Pages',
                  items: customPageItems.map((entry) => ({
                    slug: `/custom-page/${entry.ID}`,
                    externalUrl: entry.external ? entry.url : undefined,
                    displayName: entry.DisplayName,
                    icon: 'CustomPageIcon',
                  })),
                };
                return (
                  <NavItem
                    key={configEntry.key}
                    $expanded={isSidebarExpanded}
                    $active={isActive}
                    {...makeHoverHandlers(configEntry.key, flyout)}
                  >
                    <configEntry.Icon />
                    <NavLabel $visible={isSidebarExpanded}>{configEntry.label}</NavLabel>
                    {isSidebarExpanded && (
                      <NavChevron>
                        <ChevronRightIcon />
                      </NavChevron>
                    )}
                  </NavItem>
                );
              }

              const isActive = isEntryActive(configEntry);

              if (configEntry.type === 'leaf') {
                return (
                  <NavItem
                    key={configEntry.key}
                    $expanded={isSidebarExpanded}
                    $active={isActive}
                    onClick={() => navigateTo(itemsBySlug[configEntry.slug] ?? { slug: configEntry.slug })}
                    {...makeHoverHandlers(configEntry.key, resolveLeafTooltip(configEntry))}
                  >
                    <configEntry.Icon />
                    <NavLabel $visible={isSidebarExpanded}>{configEntry.label}</NavLabel>
                  </NavItem>
                );
              }

              const resolvedFlyout = resolveGroupFlyout(configEntry);
              if (!resolvedFlyout) return null;

              return (
                <NavItem
                  key={configEntry.key}
                  $expanded={isSidebarExpanded}
                  $active={isActive}
                  {...makeHoverHandlers(configEntry.key, resolvedFlyout)}
                >
                  <configEntry.Icon />
                  <NavLabel $visible={isSidebarExpanded}>{configEntry.label}</NavLabel>
                  {isSidebarExpanded && (
                    <NavChevron>
                      <ChevronRightIcon />
                    </NavChevron>
                  )}
                </NavItem>
              );
            })}
          </NavList>

          <ExpandTrigger
            className="expand-trigger"
            $expanded={isSidebarExpanded}
            style={{ opacity: isSidebarExpanded ? 1 : undefined }}
            onMouseEnter={(event) => {
              const label = isSidebarExpanded ? 'Collapse' : 'Expand';
              showFlyout('expand-toggle', event.currentTarget as HTMLElement, { kind: 'tooltip', label });
            }}
            onMouseLeave={scheduleFlyoutClose}
            onClick={() => {
              closeFlyout();
              setIsSidebarExpanded((currentValue) => !currentValue);
            }}
          >
            <ChevronRightIcon />
          </ExpandTrigger>
        </SideNavContainer>

        {activeFlyout && (
          <NavFlyout
            activeFlyout={activeFlyout}
            flyoutLeft={flyoutLeft}
            currentPath={location.pathname}
            onMouseEnter={cancelFlyoutClose}
            onMouseLeave={scheduleFlyoutClose}
            onItemClick={navigateTo}
            onTitleClick={(slug) => navigateTo({ slug })}
          />
        )}
      </>
    );
  },
);
